import type { QueryClient } from "@tanstack/react-query";
import { isFullLegalNameField } from "@/features/intake/lib/intakeFieldMatch";
import type { Radiologist } from "@/features/radiologists/types/radiologist";
import { dbSubject, subjectKey, type IntakeSubject } from "@/features/intake/lib/intakeSubject";
import type {
  AttestationAnswer,
  AttestationOut,
  IntakeItem,
  IntakeStatus,
  ITOnboardingOut,
  LegalNameUpdate,
  ReferenceEntry,
  ReferencesOut,
  SpecialtiesOut,
} from "@/features/intake/types/intake";

/**
 * In-memory intake sandbox behind the manager "preview walkthrough" — it lets an
 * admin walk the radiologist onboarding wizard end-to-end without writing
 * anything to the database or Drive.
 *
 * It works by reserving a sentinel radiologist id: every api/intake.ts function
 * dispatches here instead of the network when it's handed the preview subject,
 * so the wizard, the checklist, and all six forms run completely unmodified.
 *
 * The *shape* of the walkthrough (which documents, which basic-info fields,
 * which specialties/IT options and attestation questions) is Sheet/Doc-driven
 * and would drift if hardcoded, so each read lazily seeds itself from the real
 * endpoints for a real "seed" radiologist — then blanks every value, file, and
 * done flag so the preview starts from a clean slate. Only the configuration
 * survives seeding; none of the seed radiologist's own data is ever shown.
 */

/** Sentinel radiologist id that routes every intake call to this sandbox. */
export const PREVIEW_RAD_ID = -1;

/** The subject every preview call is made against. */
export const PREVIEW_SUBJECT: IntakeSubject = dbSubject(PREVIEW_RAD_ID);

export function isPreviewSubject(subject: IntakeSubject): boolean {
  return subject.radId === PREVIEW_RAD_ID;
}

/** Stand-in name until Step 1 mints the sandbox radiologist. */
const PREVIEW_RAD_NAME = "Preview radiologist";

/** A lazily-seeded slice of preview state. */
type Cell<T> = { value?: T; inflight?: Promise<T> };

type SandboxState = {
  /** Real radiologist whose intake *configuration* seeds the preview. */
  seed: IntakeSubject;
  /** The fake radiologist, created by Step 1 of the wizard. */
  rad: Radiologist | null;
  status: Cell<IntakeStatus>;
  references: Cell<ReferencesOut>;
  specialties: Cell<SpecialtiesOut>;
  it: Cell<ITOnboardingOut>;
  attestation: Cell<AttestationOut>;
};

let state: SandboxState | null = null;

/** Loader supplied by the api layer (which owns the HTTP calls) to seed a slice. */
type Loader<T> = (seed: IntakeSubject) => Promise<T>;

function requireState(): SandboxState {
  if (!state) throw new Error("Preview mode isn’t active.");
  return state;
}

/**
 * Begin (or restart) a preview seeded from `seed`, dropping any cached preview
 * queries so a restart genuinely starts over.
 */
export function startPreview(seed: IntakeSubject, queryClient: QueryClient): void {
  state = {
    seed,
    rad: null,
    status: {},
    references: {},
    specialties: {},
    it: {},
    attestation: {},
  };
  clearPreviewQueries(queryClient);
}

export function endPreview(queryClient: QueryClient): void {
  state = null;
  clearPreviewQueries(queryClient);
}

/** Drop every react-query entry keyed to the sandbox rad (`["intake", kind, key]`). */
function clearPreviewQueries(queryClient: QueryClient): void {
  const previewKey = subjectKey(PREVIEW_SUBJECT);
  queryClient.removeQueries({
    predicate: (q) => q.queryKey[0] === "intake" && q.queryKey[2] === previewKey,
  });
}

async function seeded<T>(cell: Cell<T>, load: Loader<T>, blank: (real: T) => T): Promise<T> {
  if (cell.value !== undefined) return cell.value;
  if (!cell.inflight) {
    cell.inflight = load(requireState().seed)
      .then((real) => {
        const blanked = blank(real);
        cell.value = blanked;
        cell.inflight = undefined;
        return blanked;
      })
      .catch((err) => {
        cell.inflight = undefined;
        throw err;
      });
  }
  return cell.inflight;
}

/** Read a slice that must already be seeded — a write can't precede its form's read. */
function loaded<T>(cell: Cell<T>): T {
  if (cell.value === undefined) throw new Error("Preview data hasn’t finished loading yet.");
  return cell.value;
}

// ---------------------------------------------------------------- radiologist

/** Step 1 of the wizard: mint the fake radiologist the rest of the preview uses. */
export function previewCreateRad(firstName: string, lastName: string): Radiologist {
  const s = requireState();
  const now = new Date().toISOString();
  const rad: Radiologist = {
    id: PREVIEW_RAD_ID,
    name: `Dr. ${lastName}`,
    first_name: firstName,
    last_name: lastName,
    capacity_rvu_per_hr: 0,
    active: true,
    enabled_site_ids: [],
    specialties: [],
    created_at: now,
    updated_at: now,
    auth0_user_ids: [],
    onboarding_completed_at: null,
  };
  s.rad = rad;
  if (s.status.value) s.status.value = { ...s.status.value, rad_name: rad.name };
  return rad;
}

/**
 * Renaming in preview: update the sandbox rad and the legal-name field, and
 * report the identity a real rename would return. Persists nothing.
 */
export function previewUpdateLegalName(
  firstName: string,
  middleName: string,
  lastName: string,
): LegalNameUpdate {
  const s = requireState();
  if (!s.rad) throw new Error("Preview radiologist hasn’t been created yet.");
  // Trim once, then derive every value from the same components. The real
  // endpoint trims server-side and builds the folder name, the record and the
  // checklist value from the trimmed halves, so a preview that trimmed only the
  // joined legal name would report an identity the backend would never return.
  const first = firstName.trim();
  const middle = middleName.trim();
  const last = lastName.trim();
  const legalName = [first, middle, last].filter(Boolean).join(" ");

  s.rad = { ...s.rad, name: `Dr. ${last}`, first_name: first, last_name: last };
  const status = s.status.value;
  if (status) {
    const legalItem = status.other_info.find(isFullLegalNameField);
    if (legalItem) updateItem(legalItem.key, (i) => ({ ...i, value: legalName }));
    if (s.status.value) s.status.value = { ...s.status.value, rad_name: s.rad.name };
  }
  return {
    rad_id: PREVIEW_RAD_ID,
    rad_key: `${last}, ${first}`,
    legal_name: legalName,
    name: s.rad.name,
    first_name: first,
    last_name: last,
  };
}

/** Finishing the walkthrough in preview: stamp the fake rad, persist nothing. */
export function previewCompleteOnboarding(): Radiologist {
  const s = requireState();
  if (!s.rad) throw new Error("Preview radiologist hasn’t been created yet.");
  s.rad = { ...s.rad, onboarding_completed_at: new Date().toISOString() };
  return s.rad;
}

// --------------------------------------------------------------------- status

function blankItem(item: IntakeItem): IntakeItem {
  return { ...item, done: false, na: false, value: null, text: null, files: [] };
}

/** Recompute the required/done/missing tallies the same way the backend does. */
function recount(status: IntakeStatus): IntakeStatus {
  const required = [...status.documents, ...status.other_info].filter((i) => !i.optional);
  const done = required.filter((i) => i.done).length;
  return {
    ...status,
    required_count: required.length,
    done_count: done,
    missing_count: required.length - done,
  };
}

/** Mirror of the backend's rule for when an item counts as satisfied. */
function withDone(item: IntakeItem): IntakeItem {
  const done =
    item.na === true ||
    item.files.length > 0 ||
    !!item.value?.trim() ||
    !!item.text?.trim();
  return { ...item, done };
}

export function previewStatus(load: Loader<IntakeStatus>): Promise<IntakeStatus> {
  const s = requireState();
  return seeded(s.status, load, (real) =>
    recount({
      ...real,
      rad_id: PREVIEW_RAD_ID,
      // Never carry the seed radiologist's real name across — before Step 1
      // mints the sandbox rad there's no name to show, so use a placeholder.
      rad_name: s.rad?.name ?? PREVIEW_RAD_NAME,
      folder_id: "",
      documents: real.documents.map(blankItem),
      other_info: real.other_info.map(blankItem),
    }),
  );
}

/** Apply `patch` to one item (in either section) and return the updated item. */
function updateItem(key: string, patch: (item: IntakeItem) => IntakeItem): IntakeItem {
  const s = requireState();
  const status = loaded(s.status);
  let updated: IntakeItem | null = null;
  const apply = (items: IntakeItem[]) =>
    items.map((i) => {
      if (i.key !== key) return i;
      updated = withDone(patch(i));
      return updated;
    });
  const documents = apply(status.documents);
  const other_info = apply(status.other_info);
  if (!updated) throw new Error(`Unknown intake item “${key}”.`);
  s.status.value = recount({ ...status, documents, other_info });
  return updated;
}

/** A stand-in for the Drive file a real upload would create. */
function fakeFile(filename: string) {
  return {
    filename,
    drive_file_id: `preview-${Math.random().toString(36).slice(2, 10)}`,
    uploaded_at: new Date().toISOString(),
    web_view_link: null,
  };
}

export function previewUploadFile(requiredKey: string, filename: string): IntakeItem {
  return updateItem(requiredKey, (i) => ({ ...i, files: [...i.files, fakeFile(filename)] }));
}

export function previewUploadText(requiredKey: string, text: string): IntakeItem {
  return updateItem(requiredKey, (i) => ({ ...i, text }));
}

export function previewReplaceFile(
  requiredKey: string,
  driveFileId: string,
  filename: string,
): IntakeItem {
  return updateItem(requiredKey, (i) => ({
    ...i,
    files: i.files.map((f) => (f.drive_file_id === driveFileId ? fakeFile(filename) : f)),
  }));
}

export function previewDeleteFile(requiredKey: string, driveFileId: string): IntakeItem {
  return updateItem(requiredKey, (i) => ({
    ...i,
    files: i.files.filter((f) => f.drive_file_id !== driveFileId),
  }));
}

export function previewSetNotApplicable(requiredKey: string, na: boolean): IntakeItem {
  return updateItem(requiredKey, (i) => ({ ...i, na }));
}

export function previewSaveInfoBatch(
  fields: { required_key: string; value: string; na?: boolean }[],
): IntakeItem[] {
  return fields.map((f) =>
    updateItem(f.required_key, (i) => ({ ...i, value: f.value, na: f.na ?? false })),
  );
}

// ----------------------------------------------------------- the other slices

export function previewReferences(load: Loader<ReferencesOut>): Promise<ReferencesOut> {
  return seeded(requireState().references, load, () => ({ references: [] }));
}

export function previewSaveReferences(references: ReferenceEntry[]): ReferencesOut {
  const s = requireState();
  s.references.value = { references: references.map((r) => ({ ...r })) };
  return s.references.value;
}

export function previewSpecialties(load: Loader<SpecialtiesOut>): Promise<SpecialtiesOut> {
  return seeded(requireState().specialties, load, (real) => ({
    options: real.options,
    selected_ids: [],
  }));
}

export function previewSaveSpecialties(selectedIds: string[]): SpecialtiesOut {
  const s = requireState();
  s.specialties.value = { ...loaded(s.specialties), selected_ids: [...selectedIds] };
  return s.specialties.value;
}

export function previewITOnboarding(load: Loader<ITOnboardingOut>): Promise<ITOnboardingOut> {
  return seeded(requireState().it, load, (real) => ({
    options: real.options,
    selected_id: null,
  }));
}

export function previewSaveITOnboarding(selectedId: string | null): ITOnboardingOut {
  const s = requireState();
  s.it.value = { ...loaded(s.it), selected_id: selectedId };
  return s.it.value;
}

export function previewAttestation(load: Loader<AttestationOut>): Promise<AttestationOut> {
  return seeded(requireState().attestation, load, (real) => ({
    questions: real.questions,
    answers: [],
  }));
}

export function previewSubmitAttestation(answers: AttestationAnswer[]): { filename: string } {
  const s = requireState();
  s.attestation.value = { ...loaded(s.attestation), answers: answers.map((a) => ({ ...a })) };
  return { filename: "Attestation (preview)" };
}
